import React, { FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Button from 'shared/components/form/button';
import CustomModal from 'shared/components/modal/modal';
import { API_CONFIG } from 'shared/constants/constants';
import httpService from 'shared/services/http.service';
import localizationConstants from 'shared/util/translation/constants';

interface SupportFormModalProps {
	show: boolean;
	onClose: () => void;
}

const MESSAGE_LIMIT = 1000;

const getPageContext = () => {
	const titleCandidate = document.querySelector('h1, h2, [data-support-item-name], .course-list-text, .card-title');
	const itemName = titleCandidate?.textContent?.trim() || '';

	return {
		pageUrl: window.location.href,
		pagePath: `${window.location.pathname}${window.location.search}`,
		pageTitle: document.title || '',
		itemName
	};
};

const SupportFormModal: React.FC<SupportFormModalProps> = ({ show, onClose }) => {
	const { t } = useTranslation();
	const [message, setMessage] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isSubmitted, setIsSubmitted] = useState(false);
	const [error, setError] = useState('');

	const trimmedMessage = useMemo(() => message.trim(), [message]);

	const handleClose = () => {
		setMessage('');
		setIsSubmitted(false);
		setError('');
		onClose();
	};

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		if (!trimmedMessage) {
			setError(String(t(localizationConstants.supportMessageRequired)));
			return;
		}

		setIsSubmitting(true);
		setError('');

		try {
			await httpService.post(API_CONFIG.path.supportRequest, {
				message: trimmedMessage,
				...getPageContext()
			});
			setIsSubmitted(true);
			setMessage('');
		} catch {
			setError(String(t(localizationConstants.somethingWentWrong)));
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<CustomModal show={show} handleClose={handleClose} className='support-form-modal'>
			{isSubmitted ? (
				<div className='support-form-modal__success text-center'>
					<h3 className='font--20px font-weight--600 mb-20'>{t(localizationConstants.supportThankYouTitle)}</h3>
					<p className='font--14px line-height--20px mb-24'>{t(localizationConstants.supportThankYouMessage)}</p>
					<Button btnType='primary' className='support-form-modal__close' onClick={handleClose}>
						{t(localizationConstants.close)}
					</Button>
				</div>
			) : (
				<form className='support-form-modal__form' onSubmit={handleSubmit}>
					<h3 className='font--20px font-weight--600 mb-20'>{t(localizationConstants.contactSupport)}</h3>
					<label className='font--13px font-weight--600 mb-8' htmlFor='support-message'>
						{t(localizationConstants.supportMessage)}
					</label>
					<textarea
						id='support-message'
						className='support-form-modal__textarea form-control'
						maxLength={MESSAGE_LIMIT}
						value={message}
						onChange={(event) => setMessage(event.target.value)}
					/>
					<div className='support-form-modal__meta d-flex justify-content-space-between align-items-center mt-4'>
						<span className='support-form-modal__error'>{error}</span>
						<span className='support-form-modal__counter'>
							{message.length}/{MESSAGE_LIMIT}
						</span>
					</div>
					<div className='d-flex justify-content-end mt-16'>
						<Button
							btnType='primary'
							type='submit'
							loading={isSubmitting}
							disabled={!trimmedMessage || isSubmitting}
							className='support-form-modal__submit'
						>
							{t(localizationConstants.send)}
						</Button>
					</div>
				</form>
			)}
		</CustomModal>
	);
};

export default SupportFormModal;
